import React, {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

interface QuillEditorProps {
  readOnly?: boolean;
  defaultValue?: string;
  value?: string;
  onTextChange?: (content: string) => void;
  onSelectionChange?: (range: any) => void;
}

const TOOLBAR_OPTIONS = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }, { background: [] }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ align: [] }],
  ["link", "image", "video"],
  ["clean"],
];

function looksLikeHTML(text: string) {
  // basic check: contains tags
  return /<\/?[a-z][\s\S]*>/i.test(text);
}

const QuillEditor = forwardRef<any, QuillEditorProps>(
  (
    { readOnly = false, defaultValue, value, onTextChange, onSelectionChange },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const quillRef = useRef<any>(null);
    const isUpdatingRef = useRef(false);

    const [internalValue, setInternalValue] = useState<string>(
      value ?? defaultValue ?? ""
    );

    useLayoutEffect(() => {
      if (quillRef.current) {
        quillRef.current.enable(!readOnly);
      }
    }, [readOnly]);

    // ✅ Controlled updates (value -> editor)
    useEffect(() => {
      if (value === undefined) return;
      if (!quillRef.current) return;

      const quill = quillRef.current;
      const currentHtml = quill.root.innerHTML;

      if (currentHtml !== value) {
        isUpdatingRef.current = true;
        quill.clipboard.dangerouslyPasteHTML(value || "", "silent");
        requestAnimationFrame(() => {
          isUpdatingRef.current = false;
        });
      }
    }, [value]);

    useEffect(() => {
      if (!containerRef.current || quillRef.current) return;

      const Quill = (window as any).Quill;

      // ✅ Enable color/background style support
      const ColorStyle = Quill.import("attributors/style/color");
      const BackgroundStyle = Quill.import("attributors/style/background");
      Quill.register(ColorStyle, true);
      Quill.register(BackgroundStyle, true);

      quillRef.current = new Quill(containerRef.current, {
        theme: "snow",
        placeholder: "Type your text here...",
        readOnly,
        modules: {
          toolbar: TOOLBAR_OPTIONS,
          clipboard: {
            matchVisual: false,
          },
        },
      });

      const quill = quillRef.current;

      // ✅ Initial HTML load
      const initial = value ?? defaultValue ?? internalValue ?? "";
      if (initial) {
        quill.clipboard.dangerouslyPasteHTML(initial, "silent");
      }

      // ✅ 100% FIX: intercept paste and force HTML conversion
      const root = quill.root;

      const onPaste = (e: ClipboardEvent) => {
        if (!e.clipboardData) return;

        const html = e.clipboardData.getData("text/html");
        const text = e.clipboardData.getData("text/plain");

        // If clipboard provides HTML → let Quill handle naturally
        if (html && html.trim().length > 0) return;

        // If clipboard gives plain text that contains html tags → convert manually
        if (text && looksLikeHTML(text)) {
          e.preventDefault();

          const range = quill.getSelection(true);
          const index = range ? range.index : quill.getLength();

          quill.clipboard.dangerouslyPasteHTML(index, text, "user");
        }
      };

      root.addEventListener("paste", onPaste as any);

      quill.on("text-change", () => {
        if (isUpdatingRef.current) return;

        const html = quill.root.innerHTML;
        setInternalValue(html);
        onTextChange?.(html);
      });

      quill.on("selection-change", (range: any) => {
        onSelectionChange?.(range);
      });

      if (ref) {
        if (typeof ref === "function") ref(quill);
        else ref.current = quill;
      }

      return () => {
        root.removeEventListener("paste", onPaste as any);
        quillRef.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div className="min-h-100px mb-2">
        <div ref={containerRef} />
      </div>
    );
  }
);

QuillEditor.displayName = "QuillEditor";
export default QuillEditor;
